
import { useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { useAccount } from "wagmi";
import { useWeb3Modal } from "@web3modal/wagmi/react";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Loader2, ChevronRight } from "lucide-react";
import { daoCategories, DaoCategory } from "@/data/daoCategories";
import { useDAODeployment } from "./hooks/useDAODeployment";
import { BlockchainConfig } from "./BlockchainConfig";
import { LaunchTrustFields } from "@/components/identity/LaunchTrustFields";
import { launchTrustConfigured } from "@/features/identity/useLaunchTrustRegistration";

// Define the form schema with Zod
const formSchema = z.object({
  name: z.string().min(3, "Name must be at least 3 characters"),
  symbol: z.string().min(1, "Symbol is required").max(8, "Symbol must be 8 characters or less"),
  initialSupply: z.string().refine((value) => Number(value) > 0, "Initial supply must be greater than zero"),
  description: z.string().optional(),
  chain: z.string().min(1, "Please select a blockchain"),
  acceptedTokens: z.string().optional(),
  deployerVeto: z.boolean().default(false),
  governorUpgradeable: z.boolean().default(false),
  multichain: z.boolean().default(false),
  additionalChains: z.array(z.string()).optional(),
  proposalThreshold: z.string().default("1000"),
  quorumPercentage: z.string().default("4"),
  votingDelay: z.string().default("1"),
  votingPeriod: z.string().default("5760"),
  daoCategories: z.array(z.string()).optional(),
  trustEnabled: z.boolean().default(false),
  founderEntityId: z.string().default(""),
  entityMetadataURI: z.string().default(""),
  projectMetadataURI: z.string().default(""),
});

type FormData = z.infer<typeof formSchema>;

export const CreateDaoForm = () => {
  const { address, isConnected } = useAccount();
  const { open } = useWeb3Modal();
  const { toast } = useToast();
  const { deployDAO, isDeploying } = useDAODeployment();
  const [deployedAddresses, setDeployedAddresses] = useState<{
    tokenAddress: string;
    governorAddress: string;
    treasuryAddress: string;
    transactionHash: string;
  } | null>(null);

  // Initialize form
  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      symbol: "",
      initialSupply: "1000000",
      description: "",
      chain: "polygon",
      acceptedTokens: "USDC,MATIC",
      deployerVeto: true,
      governorUpgradeable: false,
      multichain: false,
      additionalChains: [],
      proposalThreshold: "1000",
      quorumPercentage: "4",
      votingDelay: "1",
      votingPeriod: "5760",
      daoCategories: [],
      trustEnabled: launchTrustConfigured,
      founderEntityId: "",
      entityMetadataURI: "",
      projectMetadataURI: "",
    },
  });

  // Watch multichain field to show/hide additional chains
  const watchMultichain = form.watch("multichain");
  const selectedDaoCategories = form.watch("daoCategories") || [];

  const onSubmit = async (data: FormData) => {
    if (!isConnected) {
      open();
      return;
    }

    try {
      const result = await deployDAO(data);
      
      console.log("Deploy DAO result:", result);
      
      if (result?.success) {
        const deployment = {
          tokenAddress: result.tokenAddress || "Address unavailable — see transaction logs",
          governorAddress: result.governorAddress || "Address unavailable — see transaction logs",
          treasuryAddress: result.treasuryAddress || "Address unavailable — see transaction logs",
          transactionHash: result.transactionHash,
        };
        setDeployedAddresses(deployment);

        toast({
          title: "DAO Created Successfully!",
          description: `Token: ${deployment.tokenAddress} · Governor: ${deployment.governorAddress} · Treasury: ${deployment.treasuryAddress}`,
        });

        window.dispatchEvent(new CustomEvent('daoCreated'));
        form.reset();
        window.setTimeout(() => document.getElementById("dao-deployment-result")?.scrollIntoView({ behavior: "smooth", block: "start" }), 0);
      }
    } catch (error) {
      console.error("DAO Creation Error:", error);
      toast({
        title: "Error Creating DAO",
        description: "There was a problem creating your DAO. Please try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <Card>
      <CardContent className="pt-6">
        {deployedAddresses && (
          <div id="dao-deployment-result" className="mb-6 p-4 bg-primary/10 border border-primary/20 rounded-lg space-y-3">
            <h3 className="font-semibold text-lg">DAO Deployed Successfully! 🎉</h3>
            <a
              className="text-sm text-primary underline break-all"
              href={`https://polygonscan.com/tx/${deployedAddresses.transactionHash}`}
              target="_blank"
              rel="noreferrer"
            >
              View deployment transaction
            </a>
            <div className="space-y-2 text-sm">
              <div>
                <span className="font-medium">Governor Address:</span>
                <p className="font-mono bg-background/50 p-2 rounded mt-1 break-all">{deployedAddresses.governorAddress}</p>
              </div>
              <div>
                <span className="font-medium">Token Address:</span>
                <p className="font-mono bg-background/50 p-2 rounded mt-1 break-all">{deployedAddresses.tokenAddress}</p>
              </div>
              <div>
                <span className="font-medium">Treasury Address:</span>
                <p className="font-mono bg-background/50 p-2 rounded mt-1 break-all">{deployedAddresses.treasuryAddress}</p>
              </div>
            </div>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => setDeployedAddresses(null)}
              className="mt-2"
            >
              Create Another DAO
            </Button>
          </div>
        )}
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <LaunchTrustFields
              entityLabel="DAO"
              value={{
                trustEnabled: form.watch("trustEnabled"),
                founderEntityId: form.watch("founderEntityId"),
                entityMetadataURI: form.watch("entityMetadataURI"),
                projectMetadataURI: form.watch("projectMetadataURI"),
              }}
              onChange={(trust) => {
                form.setValue("trustEnabled", trust.trustEnabled);
                form.setValue("founderEntityId", trust.founderEntityId);
                form.setValue("entityMetadataURI", trust.entityMetadataURI);
                form.setValue("projectMetadataURI", trust.projectMetadataURI);
              }}
            />
            {/* Basic DAO information */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium">Basic Information</h3>
              
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>DAO Name</FormLabel>
                    <FormControl>
                      <Input placeholder="My Awesome DAO" {...field} />
                    </FormControl>
                    <FormDescription>
                      The name of your decentralized autonomous organization
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="symbol"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Token Symbol</FormLabel>
                    <FormControl>
                      <Input placeholder="DAO" {...field} maxLength={8} />
                    </FormControl>
                    <FormDescription>
                      The symbol for your governance token (max 8 characters)
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="initialSupply"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Initial Governance Token Supply</FormLabel>
                    <FormControl>
                      <Input type="number" min="1" step="any" placeholder="1000000" {...field} />
                    </FormControl>
                    <FormDescription>
                      The total number of governance tokens minted to your wallet when the DAO is created.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description</FormLabel>
                    <FormControl>
                      <Textarea 
                        placeholder="Describe the purpose and goals of your DAO" 
                        {...field} 
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* DAO Categories selection */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium">DAO Categories</h3>
              <FormField
                control={form.control}
                name="daoCategories"
                render={() => (
                  <FormItem>
                    <FormDescription>
                      Select one or more categories that best describe your DAO.
                    </FormDescription>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {daoCategories.map((category: DaoCategory) => (
                        <FormField
                          key={category.id}
                          control={form.control}
                          name="daoCategories"
                          render={({ field }) => {
                            return (
                              <FormItem
                                key={category.id}
                                className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4"
                              >
                                <FormControl>
                                  <Checkbox
                                    checked={field.value?.includes(category.id)}
                                    onCheckedChange={(checked) => {
                                      if (category.id === "governance" && !checked) {
                                        form.setValue("governorUpgradeable", false);
                                      }
                                      return field.onChange(
                                        checked
                                          ? [...(field.value || []), category.id]
                                          : field.value?.filter((value) => value !== category.id)
                                      );
                                    }}
                                  />
                                </FormControl>
                                <div className="space-y-1 leading-none">
                                  <FormLabel className="font-normal">
                                    <span className="mr-2">{category.icon}</span>
                                    {category.name}
                                  </FormLabel>
                                  <FormDescription>
                                    {category.description}
                                  </FormDescription>
                                </div>
                              </FormItem>
                            );
                          }}
                        />
                      ))}
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />
              {selectedDaoCategories.includes("governance") && (
                <FormField
                  control={form.control}
                  name="governorUpgradeable"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border border-primary/30 bg-primary/5 p-4">
                      <FormControl>
                        <Checkbox
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                      <div className="space-y-1 leading-none">
                        <FormLabel>Make Governor Contract Upgradeable</FormLabel>
                        <FormDescription>
                          Deploy the governor behind a UUPS proxy. Upgrades require an approved DAO proposal and timelock execution; the deployer and factory cannot upgrade it directly.
                        </FormDescription>
                      </div>
                    </FormItem>
                  )}
                />
              )}
            </div>
            
            {/* Blockchain configuration */}
            <BlockchainConfig control={form.control} watchMultichain={watchMultichain} />
            
            {/* Advanced governance settings */}
            <Accordion type="single" collapsible className="w-full">
              <AccordionItem value="governance">
                <AccordionTrigger>
                  <span className="text-lg font-medium">Advanced Governance Settings</span>
                </AccordionTrigger>
                <AccordionContent className="space-y-4 pt-4">
                  <FormField
                    control={form.control}
                    name="proposalThreshold"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Proposal Threshold</FormLabel>
                        <FormControl>
                          <Input type="number" {...field} />
                        </FormControl>
                        <FormDescription>
                          Minimum number of tokens required to make a proposal
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <FormField
                    control={form.control}
                    name="quorumPercentage"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Quorum Percentage</FormLabel>
                        <FormControl>
                          <Input type="number" {...field} min="1" max="100" />
                        </FormControl>
                        <FormDescription>
                          Percentage of tokens that must vote for a proposal to pass
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <FormField
                    control={form.control}
                    name="votingDelay"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Voting Delay (blocks)</FormLabel>
                        <FormControl>
                          <Input type="number" {...field} min="1" />
                        </FormControl>
                        <FormDescription>
                          Number of blocks to wait before voting begins
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <FormField
                    control={form.control}
                    name="votingPeriod"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Voting Period (blocks)</FormLabel>
                        <FormControl>
                          <Input type="number" {...field} min="1" />
                        </FormControl>
                        <FormDescription>
                          Duration of voting in blocks (approx. 5760 blocks = 1 day on Ethereum)
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <FormField
                    control={form.control}
                    name="deployerVeto"
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
                        <FormControl>
                          <Checkbox
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                        <div className="space-y-1 leading-none">
                          <FormLabel>
                            Enable Deployer Veto Power
                          </FormLabel>
                          <FormDescription>
                            Grant the deployer temporary veto power over DAO proposals (can be renounced later)
                          </FormDescription>
                        </div>
                      </FormItem>
                    )}
                  />
                </AccordionContent>
              </AccordionItem>
            </Accordion>
            
            {/* Submit button */}
            <Button 
              type="submit" 
              className="w-full"
              disabled={isDeploying || !isConnected}
            >
              {isDeploying ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creating DAO...
                </>
              ) : (
                <>
                  Create DAO
                  <ChevronRight className="ml-2 h-4 w-4" />
                </>
              )}
            </Button>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
};
